import type { ResponseGetMerchflowsCategories } from "@CommonApi/merchflows/categories";
import type { ParamsGetInputsProductsAll } from "@CommonApi/inputs/products/all";
import type { MerchflowsMultiValue } from "src/merchflows/shared/multiValue";
import { normalizeMultiValue } from "src/merchflows/shared/multiValue";

export type ProductCategoriesMap = ResponseGetMerchflowsCategories;

export type ProductsSearchParamsInput = Omit<
  ParamsGetInputsProductsAll,
  "size" | "page" | "brand" | "category" | "subcategory"
> & {
  page?: ParamsGetInputsProductsAll["page"] | string;
  brand?: string;
  category?: string;
  subcategory?: string;
};

type ProductsFilterQueryParams = Pick<
  ParamsGetInputsProductsAll,
  "upc" | "product_code" | "name" | "brand" | "category" | "subcategory"
>;

const sortValues = (values: Iterable<string>) =>
  Array.from(values).sort((left, right) => left.localeCompare(right));

export const parseCsvSearchParam = <T extends string>(value: string | undefined) => {
  if (!value) {
    return undefined;
  }

  return normalizeMultiValue<T>(value.split(",") as T[]);
};

export const serializeCsvSearchParam = <T extends string>(values: MerchflowsMultiValue<T>) => {
  const normalizedValues = normalizeMultiValue(values);
  return normalizedValues ? normalizedValues.join(",") : undefined;
};

export const filterMultiSelectValues = <T extends string>(
  values: MerchflowsMultiValue<T>,
  allowedValues: readonly string[],
) => {
  const normalizedValues = normalizeMultiValue(values);
  if (!normalizedValues?.length) {
    return undefined;
  }

  const allowedValuesSet = new Set(allowedValues);
  const filteredValues = normalizedValues.filter((value) => allowedValuesSet.has(value));

  return filteredValues.length > 0 ? filteredValues : undefined;
};

export const getProductsCategoryOptions = (availableCategories: ProductCategoriesMap) => {
  return sortValues(Object.keys(availableCategories));
};

export const getAvailableProductSubcategoryOptions = (
  availableCategories: ProductCategoriesMap,
  categories: readonly string[] | undefined,
) => {
  const subcategories = new Set<string>();

  if (!categories?.length) {
    Object.values(availableCategories).forEach((categorySubcategories) => {
      categorySubcategories.forEach((subcategory) => subcategories.add(subcategory));
    });
  } else {
    categories.forEach((category) => {
      (availableCategories[category] || []).forEach((subcategory) =>
        subcategories.add(subcategory),
      );
    });
  }

  return sortValues(subcategories);
};

export const getProductsSubcategoryOptions = (
  availableCategories: ProductCategoriesMap,
  categories: readonly string[] | undefined,
) => {
  return getAvailableProductSubcategoryOptions(availableCategories, categories);
};

export const buildProductsFilterSearchParams = ({
  currentSearchParams,
  filterQueryParams,
  availableCategories,
  canValidateSubcategories,
}: {
  currentSearchParams: ProductsSearchParamsInput;
  filterQueryParams: ProductsFilterQueryParams;
  availableCategories: ProductCategoriesMap;
  canValidateSubcategories: boolean;
}): ProductsSearchParamsInput => {
  const normalizedCategory = normalizeMultiValue(filterQueryParams.category);
  const normalizedSubcategory = normalizeMultiValue(filterQueryParams.subcategory);
  const nextSubcategories = canValidateSubcategories
    ? filterMultiSelectValues(
        normalizedSubcategory,
        getAvailableProductSubcategoryOptions(availableCategories, normalizedCategory),
      )
    : normalizedSubcategory;

  return {
    ...currentSearchParams,
    page: 1,
    upc: filterQueryParams.upc,
    product_code: filterQueryParams.product_code,
    name: filterQueryParams.name,
    brand: typeof filterQueryParams.brand === "string" ? filterQueryParams.brand : undefined,
    category: serializeCsvSearchParam(normalizedCategory),
    subcategory: serializeCsvSearchParam(nextSubcategories),
  };
};
